/**
 * Boots a Phaser.Game into a parent element. Called once by the React host
 * component (GameCanvas) on mount; the returned instance is destroyed on unmount.
 * RESIZE scale mode fills the parent and is DPR-aware (Phaser v4 handles
 * devicePixelRatio internally). Arcade physics uses a fixed step (the player ball
 * is an arcade image for convenience; gameplay collision lives in core).
 *
 * The game starts on {@link StartFlowScene} (drag-to-start + mode select), which
 * hands off to {@link MainScene} with the chosen {@link import('../core').GameMode}.
 *
 * Two boot modes share one config builder:
 *  - {@link bootGame}   — the live game (StartFlowScene → MainScene).
 *  - {@link bootReplay} — deterministic playback of a stored {@link RunRecord}
 *    via {@link ReplayScene} (the third of "one sim, three jobs"). The scene is
 *    registered by key here so a host can also `scene.start(REPLAY_SCENE_KEY,
 *    { record })` to re-launch with a different run without rebooting the game.
 */
import Phaser from 'phaser';
import { StartFlowScene } from './StartFlowScene';
import { MainScene } from './MainScene';
import { ReplayScene } from './ReplayScene';
import type { ReplaySceneData } from './ReplayScene';
import type { GameBus } from './events';
import type { RunRecord } from '../core';

/**
 * Shared arcade-physics config (one fixed 60 Hz step) used by both the live game
 * and replays so the deterministic core advances identically in each.
 */
const PHYSICS: Phaser.Types.Core.PhysicsConfig = {
  default: 'arcade',
  arcade: {
    gravity: { x: 0, y: 0 },
    fps: 60,
    fixedStep: true,
    debug: false,
  },
};

/** Shared scale config: RESIZE fills the parent and is DPR-aware. */
const SCALE = {
  mode: Phaser.Scale.RESIZE,
  width: '100%',
  height: '100%',
} as const;

/**
 * Boot the live game. The {@link ReplayScene} is also registered (inactive) so the
 * same game instance could switch to playback via `scene.start(REPLAY_SCENE_KEY,
 * { record })`.
 *
 * `directRun` picks the starting scene:
 *  - false (default) — begin on {@link StartFlowScene} (the menu's teased ball +
 *    drag-to-start); it hands off to {@link MainScene} on commit.
 *  - true — begin STRAIGHT in {@link MainScene} (solo). The React shell uses this for
 *    "Play Again": a fresh canvas guarantees a clean run every time, with no reliance
 *    on a restart command reaching the (possibly transitioning) live scene.
 */
export function bootGame(
  parent: HTMLElement,
  bus: GameBus,
  skinId: string,
  directRun = false,
  forcedSeed?: number,
): Phaser.Game {
  // A forced seed (Daily Run — the day's shared seed) only applies to a direct
  // boot; the menu's teased-ball flow always rolls a fresh random seed.
  const liveScene = directRun
    ? new MainScene(bus, skinId, 'solo', undefined, undefined, forcedSeed)
    : new StartFlowScene(bus, skinId);
  const config: Phaser.Types.Core.GameConfig = {
    type: Phaser.AUTO,
    parent,
    backgroundColor: '#0b0f1a',
    scale: { ...SCALE },
    physics: PHYSICS,
    render: { antialias: true },
    // The live scene (menu flow or a direct run) auto-starts; ReplayScene is
    // registered (auto-start off) so it can be launched by key.
    scene: [liveScene, new ReplayScene()],
  };

  return new Phaser.Game(config);
}

/**
 * Boot a dedicated REPLAY game that starts straight on {@link ReplayScene}, playing
 * back the supplied {@link RunRecord} through the deterministic core (no live game /
 * StartFlowScene). Used by the React shell's replay host (a separate, short-lived
 * canvas mount) so watching a replay never disturbs the live game.
 *
 * The record is baked into the scene instance via its constructor; the scene's
 * `init()` resolves it (scene-data, if any, would override — there is none here).
 *
 * @param parent the DOM element to mount the canvas into.
 * @param record the stored run to replay (seed + input timeline + viewport + mode).
 * @param skinId the ball skin to render the replayed player with.
 * @returns the Phaser.Game instance (destroy it on unmount).
 */
export function bootReplay(parent: HTMLElement, record: RunRecord, skinId: string): Phaser.Game {
  const data: ReplaySceneData = { record, skinId };
  const config: Phaser.Types.Core.GameConfig = {
    type: Phaser.AUTO,
    parent,
    backgroundColor: '#0b0f1a',
    scale: { ...SCALE },
    physics: PHYSICS,
    render: { antialias: true },
    // Boot straight into the replay; the record is baked into the scene instance,
    // which Phaser auto-starts (it's the only / first registered scene).
    scene: [new ReplayScene(data)],
  };

  return new Phaser.Game(config);
}
